import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import VehicleListSidebar from './VehicleListSidebar';
import { FaList, FaMapMarkedAlt, FaSync } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { GetFromApi } from '../utils/api';
import socketService from '../utils/socket';
import { getRouteFromOSRM, calculateBearing } from '../utils/routing';

import carIconRunning from '../images/car.png';
import carIconStopped from '../images/car.png';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom hook for smooth marker updates
const AnimatedMarker = ({ position, children, icon, heading, vehicleId }) => {
  const markerRef = useRef(null);
  const prevPositionRef = useRef(position);

  useEffect(() => {
    if (position[0] === prevPositionRef.current[0] && position[1] === prevPositionRef.current[1]) {
      return;
    }

    const marker = markerRef.current;
    if (!marker) return;

    const startPos = prevPositionRef.current;
    const endPos = position;
    const duration = 1500;
    const startTime = Date.now();
    let frameId;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const lat = startPos[0] + (endPos[0] - startPos[0]) * easeOut;
      const lng = startPos[1] + (endPos[1] - startPos[1]) * easeOut;

      marker.setLatLng([lat, lng]);

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      } else {
        prevPositionRef.current = endPos;
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [position]);

  return (
    <Marker ref={markerRef} position={prevPositionRef.current} icon={icon}>
      {children}
    </Marker>
  );
};

// Map view updater for selected vehicle
const MapViewUpdater = ({ selectedVehicle, allVehicles }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedVehicle) {
      const vehicle = allVehicles.find(v => v.id === selectedVehicle);
      if (vehicle && vehicle.position) {
        map.flyTo(vehicle.position, 15, { duration: 1 });
      }
    }
  }, [selectedVehicle, map, allVehicles]);
  
  return null;
};

const LiveTrack = () => {
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [allVehicles, setAllVehicles] = useState([]);
  const [roadRoutes, setRoadRoutes] = useState({});

  // Fetch road-accurate route for a vehicle
  const fetchRoadRoute = useCallback(async (vehicleId, positions) => {
    if (positions.length < 2) return;
    
    try {
      const routePoints = await getRouteFromOSRM(positions);
      setRoadRoutes(prev => ({
        ...prev,
        [vehicleId]: routePoints
      }));
    } catch (error) {
      console.warn('Could not fetch road route:', error);
    }
  }, []);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const response = await GetFromApi('/vehicles');
      if (response.success) {
        const vehiclesWithPosition = await Promise.all(response.data.map(async (vehicle, index) => {
          const lat = vehicle.lat ? parseFloat(vehicle.lat) : 23.0225 + (index * 0.008);
          const lng = vehicle.lng ? parseFloat(vehicle.lng) : 72.5714 + (index * 0.008);
          
          let historyCoords = [[lat, lng]];
          try {
            if (vehicle.device_id) {
              const histRes = await GetFromApi(`/history/${vehicle.device_id}`);
              if (Array.isArray(histRes)) {
                // Filter out invalid coords and reverse to chronological order
                const validPoints = histRes
                  .filter(r => r.latitude && r.longitude && parseFloat(r.latitude) !== 0)
                  .map(r => [parseFloat(r.latitude), parseFloat(r.longitude)])
                  .reverse();
                
                // Filter duplicate coordinates / signal drift
                const uniquePoints = [];
                for (const p of validPoints) {
                  if (uniquePoints.length === 0) {
                    uniquePoints.push(p);
                  } else {
                    const last = uniquePoints[uniquePoints.length - 1];
                    const dist = Math.abs(p[0] - last[0]) + Math.abs(p[1] - last[1]);
                    if (dist > 0.0001) { // Genuinely moved > 10m
                      uniquePoints.push(p);
                    }
                  }
                }
                
                if (uniquePoints.length > 0) {
                  historyCoords = uniquePoints;
                }
              }
            }
          } catch (err) {
            console.error("Failed to load vehicle history:", err);
          }
          
          return {
            ...vehicle,
            position: [lat, lng],
            routeHistory: historyCoords,
            routeColor: vehicle.status === 'active' ? '#3B82F6' : '#EF4444',
            source: vehicle.address || 'Current Location',
            destination: 'In Transit',
            lastUpdated: vehicle.lastUpdate ? new Date(vehicle.lastUpdate).toLocaleTimeString() : 'Just now',
            heading: vehicle.heading || 0,
            odometer: vehicle.odometer || 0,
            int_voltage: vehicle.int_voltage || 0,
            ext_voltage: vehicle.ext_voltage || 0
          };
        }));

        // Snap routes to OSRM roads
        vehiclesWithPosition.forEach(v => {
          if (v.routeHistory && v.routeHistory.length >= 2) {
            fetchRoadRoute(v.id, v.routeHistory);
          }
        });

        setAllVehicles(vehiclesWithPosition);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();

    // Subscribe to real-time updates
    socketService.connect();
    socketService.on('vehicle:location', (data) => {
      setAllVehicles(prev => prev.map(v => {
        if (v.id === data.vehicleId) {
          const newLat = data.lat;
          const newLng = data.lng;
          const oldPosition = v.position;
          const newPosition = [newLat, newLng];

          // Calculate distance moved to detect GPS drift vs. real movement
          const latDiff = Math.abs(newLat - oldPosition[0]);
          const lngDiff = Math.abs(newLng - oldPosition[1]);
          // ~0.0001 degrees ≈ 11 meters — only record route point if actually moved
          const hasMovedSignificantly = latDiff > 0.0001 || lngDiff > 0.0001;
          
          // Only append to route history when vehicle actually moves (stops drift zigzag)
          const newHistory = hasMovedSignificantly
            ? [...(v.routeHistory || []), newPosition].slice(-30)
            : (v.routeHistory || []);
          
          // Fetch road-snapped route only when route history changes
          if (hasMovedSignificantly && newHistory.length >= 2) {
            fetchRoadRoute(v.id, newHistory);
          }
          
          // Use txn-based movement flag (from official SIWI protocol: A/B/F=moving, E/G=stopped)
          // Fall back to speed threshold if txn not available
          const isRunning = data.isMoving !== undefined ? data.isMoving : (data.speed > 3);
          
          // Use device-reported heading (GPS bearing from packet field 17) when moving;
          // keep current heading when stopped to avoid icon flip on stationary drift
          const bearing = (isRunning && hasMovedSignificantly) 
            ? (data.heading || calculateBearing(oldPosition, newPosition)) 
            : (v.heading || 0);
          
          return {
            ...v,
            position: newPosition,
            speed: data.speed,
            heading: bearing,
            status: isRunning ? 'active' : 'maintenance',
            ignition: data.ignition !== undefined ? data.ignition : v.ignition,
            routeHistory: newHistory,
            lastUpdated: 'Just now',
            odometer: data.odometer !== undefined ? data.odometer : v.odometer,
            int_voltage: data.int_voltage !== undefined ? data.int_voltage : v.int_voltage,
            ext_voltage: data.ext_voltage !== undefined ? data.ext_voltage : v.ext_voltage
          };
        }
        return v;
      }));
      setLastUpdated(new Date());
    });

    return () => {
      socketService.off('vehicle:location');
    };
  }, [fetchRoadRoute]);

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const getMarkerColor = (vehicle) => {
    if (vehicle.speed > 3) return '#22C55E'; // Green for running
    if (vehicle.ignition) return '#F59E0B';  // Orange/Yellow for idling
    return '#EF4444';                         // Red for stopped
  };

  const createCustomIcon = (vehicle) => {
    const rotation = vehicle.heading || 0;
    const iconImg = vehicle.status === 'active' ? carIconRunning : carIconStopped;
    
    return L.divIcon({
      className: 'custom-vehicle-marker',
      html: `
        <div style="position: relative; width: 50px; height: 50px;">
          <div style="
            position: absolute; 
            top: 50%; 
            left: 50%; 
            transform: translate(-50%, -50%) rotate(${rotation}deg);
            transition: transform 0.5s ease;
          ">
            <img src="${iconImg}" alt="Car" style="width: 40px; height: 40px;" />
          </div>
          <div style="
            position: absolute; 
            top: -15px; 
            left: 50%; 
            transform: translateX(-50%);
            background: ${vehicle.speed > 3 ? '#22C55E' : (vehicle.ignition ? '#F59E0B' : '#EF4444')};
            color: white;
            border-radius: 10px; 
            padding: 2px 8px; 
            font-size: 10px; 
            font-weight: bold; 
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            white-space: nowrap;
          ">
            ${vehicle.number} • ${vehicle.speed || 0} km/h
          </div>
        </div>
      `,
      iconSize: [50, 50],
      iconAnchor: [25, 25],
      popupAnchor: [0, -25]
    });
  };

  return (
    <div className="position-relative h-100">
      <div className="h-100 w-100">
        <MapContainer 
          center={[23.0225, 72.5714]}
          zoom={11} 
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street Map">
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="Satellite View">
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Hybrid View">
              <TileLayer
                attribution='&copy; Google'
                url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Terrain Map">
              <TileLayer
                attribution='Map data: &copy; OpenTopoMap contributors'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Dark Mode Map">
              <TileLayer
                attribution='&copy; CartoDB'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          
          {allVehicles.map(vehicle => (
            <React.Fragment key={vehicle.id}>
              {/* Road-accurate route if available, otherwise use history */}
              {(roadRoutes[vehicle.id] || vehicle.routeHistory)?.length > 1 && (
                <Polyline
                  positions={roadRoutes[vehicle.id] || vehicle.routeHistory}
                  pathOptions={{
                    color: vehicle.speed > 0 ? '#3B82F6' : '#EF4444',
                    weight: 4,
                    opacity: 0.8,
                    lineCap: 'round',
                    lineJoin: 'round'
                  }}
                />
              )}
              
              <AnimatedMarker
                position={vehicle.position}
                icon={createCustomIcon(vehicle)}
                heading={vehicle.heading}
                vehicleId={vehicle.id}
              >
                <Popup className="vehicle-popup">
                  <div className="p-2">
                    <h6 className="mb-2 pb-2 border-bottom">{vehicle.number}</h6>
                    <p className="mb-1">
                      <strong>Status:</strong>{' '}
                      <span style={{ color: getMarkerColor(vehicle) }}>
                        {vehicle.speed > 3 ? '🟢 Running' : (vehicle.ignition ? '🟡 Idle (Engine On)' : '🔴 Stopped')}
                      </span>
                    </p>
                    <p className="mb-1">
                      <strong>Ignition:</strong>{' '}
                      <span style={{ color: vehicle.ignition ? '#22C55E' : '#EF4444', fontWeight: 'bold' }}>
                        {vehicle.ignition ? 'ON' : 'OFF'}
                      </span>
                    </p>
                    <p className="mb-1"><strong>Driver:</strong> {vehicle.driver_name || 'N/A'}</p>
                    <p className="mb-1"><strong>Speed:</strong> {vehicle.speed || 0} km/h</p>
                    <p className="mb-1"><strong>Heading:</strong> {Math.round(vehicle.heading || 0)}°</p>
                    <p className="mb-1"><strong>Last Update:</strong> {vehicle.lastUpdated}</p>
                    <p className="mb-1"><strong>Odometer:</strong> {parseFloat(vehicle.odometer || 0).toFixed(1)} km</p>
                    {vehicle.int_voltage > 0 && <p className="mb-1"><strong>Battery Voltage:</strong> {parseFloat(vehicle.int_voltage).toFixed(1)} V</p>}
                    {vehicle.ext_voltage > 0 && <p className="mb-1"><strong>Mains Voltage:</strong> {parseFloat(vehicle.ext_voltage).toFixed(1)} V</p>}
                  </div>
                </Popup>
              </AnimatedMarker>
            </React.Fragment>
          ))}
          
          <MapViewUpdater selectedVehicle={selectedVehicleId} allVehicles={allVehicles} />
        </MapContainer>
      </div>
      
      {!showSidebar && (
        <Button 
          variant="primary" 
          className="position-absolute top-0 start-0 m-3"
          onClick={toggleSidebar}
          style={{ borderRadius: '8px', zIndex: 1500 }}
        >
          <FaList className="me-2" /> Vehicles ({allVehicles.length})
        </Button>
      )}
      
      {showSidebar && (
        <div style={{ zIndex: 1500, position: 'absolute', top: 0, left: 0, height: '100%' }}>
          <VehicleListSidebar onClose={toggleSidebar} onVehicleSelect={handleVehicleSelect} />
        </div>
      )}
      
      <div className="position-absolute top-0 end-0 m-3 d-flex" style={{ zIndex: 1500 }}>
        <Button 
          variant="light" 
          className="me-2 shadow-sm"
          onClick={fetchVehicles}
          disabled={isLoading}
        >
          {isLoading ? <Spinner size="sm" animation="border" /> : <FaSync />}
        </Button>
        <Button variant="light" className="shadow-sm">
          <FaMapMarkedAlt />
        </Button>
      </div>
      
      <div 
        className="position-absolute bottom-0 end-0 m-3 bg-white p-2 rounded shadow-sm" 
        style={{ zIndex: 1500 }}
      >
        <small>
          <strong>Live:</strong> {lastUpdated.toLocaleTimeString()} | 
          <span className="text-success ms-1">{allVehicles.filter(v => v.status === 'active' || v.status === 'running').length} running</span> / 
          <span className="text-danger ms-1">{allVehicles.filter(v => v.status !== 'active' && v.status !== 'running').length} stopped</span>
        </small>
      </div>
    </div>
  );
};

export default LiveTrack;